import type { PublicAudioSource } from "@hitster/shared";
import { createEmbeddedStreamProvider } from "./EmbeddedStreamProvider";

export interface AudioProvider {
  load(source: PublicAudioSource): Promise<void>;
  play(): Promise<void>;
  pause(): void;
  stop(): void;
  seek(seconds: number): void;
  setVolume(volume: number): void;
}

class HtmlAudioProvider implements AudioProvider {
  private audio: HTMLAudioElement | null = null;
  private clipStart = 0;
  private clipEnd = 0;
  private level = 1;
  private onTimeUpdate: (() => void) | null = null;

  private waitForSeek(seconds: number): Promise<void> {
    return new Promise((resolve) => {
      if (!this.audio) return resolve();
      const audio = this.audio;
      if (seconds <= 0 || Math.abs(audio.currentTime - seconds) < 0.05) return resolve();
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        audio.removeEventListener("seeked", onSeeked);
        resolve();
      };
      const onSeeked = () => done();
      // Toujours résoudre : certains flux (MusicGuess) ne sont pas seekables.
      const timer = window.setTimeout(() => done(), 800);
      audio.addEventListener("seeked", onSeeked);
      try {
        audio.currentTime = seconds;
      } catch {
        done();
      }
    });
  }

  setVolume(volume: number): void {
    this.level = Math.max(0, Math.min(1, volume));
    if (!this.audio) return;
    this.audio.volume = this.level;
    this.audio.muted = this.level === 0;
  }

  private attachAudio(el: HTMLAudioElement): void {
    this.audio = el;
    this.audio.volume = this.level;
    this.audio.muted = this.level === 0;
  }

  async load(source: PublicAudioSource): Promise<void> {
    this.stop();
    const el = new Audio(source.url.split("#")[0]!);
    el.preload = "auto";
    if (!source.url.startsWith("/")) {
      el.crossOrigin = "anonymous";
    }
    this.attachAudio(el);
    this.clipStart = source.clipStartSeconds;
    this.clipEnd = source.clipStartSeconds + source.clipDurationSeconds;
    const isMusicGuess = source.url.startsWith("/media/mg/");
    const loadTimeoutMs = isMusicGuess ? 90_000 : 12_000;

    const loadOnce = (): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        if (!this.audio) return reject(new Error("Audio indisponible"));
        const audio = this.audio;
        let settled = false;
        const finish = (ok: boolean, err?: Error) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          audio.removeEventListener("canplay", onReady);
          audio.removeEventListener("loadeddata", onReady);
          audio.removeEventListener("error", onError);
          if (ok) resolve();
          else reject(err ?? new Error("Chargement audio échoué"));
        };
        const onReady = () => finish(true);
        const onError = () => finish(false, new Error("Chargement audio échoué"));
        const timer = window.setTimeout(() => {
          if (audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) finish(true);
          else finish(false, new Error("Chargement audio trop lent"));
        }, loadTimeoutMs);
        audio.addEventListener("canplay", onReady, { once: true });
        audio.addEventListener("loadeddata", onReady, { once: true });
        audio.addEventListener("error", onError, { once: true });
      });

    let lastError: Error | undefined;
    const attempts = isMusicGuess ? 2 : 1;
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      if (attempt > 1 && this.audio) {
        this.audio.src = source.url.split("#")[0]!;
        this.audio.load();
      }
      try {
        await loadOnce();
        this.setVolume(this.level);
        return;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error("Chargement audio échoué");
        if (attempt < attempts) await new Promise((r) => setTimeout(r, 600));
      }
    }
    throw lastError ?? new Error("Chargement audio échoué");
  }

  async play(): Promise<void> {
    if (!this.audio) throw new Error("Aucun audio chargé");
    if (this.level === 0) return;

    if (this.audio && this.onTimeUpdate) {
      this.audio.removeEventListener("timeupdate", this.onTimeUpdate);
      this.onTimeUpdate = null;
    }

    this.setVolume(this.level);
    this.audio.pause();
    await this.waitForSeek(this.clipStart);

    this.onTimeUpdate = () => {
      if (this.audio && this.audio.currentTime >= this.clipEnd) {
        this.pause();
      }
    };
    this.audio.addEventListener("timeupdate", this.onTimeUpdate);
    await this.audio.play();
  }

  pause(): void {
    this.audio?.pause();
  }

  stop(): void {
    if (this.audio && this.onTimeUpdate) {
      this.audio.removeEventListener("timeupdate", this.onTimeUpdate);
      this.onTimeUpdate = null;
    }
    if (this.audio) {
      this.audio.pause();
      this.audio.src = "";
      this.audio = null;
    }
  }

  seek(seconds: number): void {
    if (this.audio) this.audio.currentTime = seconds;
  }
}

export function createAudioProvider(source: PublicAudioSource): AudioProvider {
  if (source.streamProvider) return createEmbeddedStreamProvider();
  return new HtmlAudioProvider();
}
